import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState, useAppDispatch } from '../../store';
import { setPreferences, completeOnboarding } from '../../store/actions/OnboardingActions';
import { fetchMatterDropdowns } from '../../store/actions/Matters/MatterActions';
import Button from '../shared/TailwindComponents/Button';
import { AVAILABLE_JURISDICTIONS } from '../Matters/matterConstants';
import Dropdown, { DropdownOption } from '../shared/TailwindComponents/Dropdown';
import CheckBox from '../shared/TailwindComponents/CheckBox';
import { Well } from '../shared/brand/Field';
import FieldShell, { FOCUS_RING, FIELD_LABEL_CLASS } from '../shared/TailwindComponents/FieldShell';
import { SELECTED_CARD } from '../shared/brand/selectedCard';
import { FormActions } from '../shared/FormComponents';
import OnboardingStepCard from './OnboardingStepCard';
import { PiSunLight, PiMoonLight, PiDesktopLight } from 'react-icons/pi';
import { changeLanguage } from '../../i18n/i18n';

const SUPPORTED_LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'sv', label: 'Svenska' },
  { value: 'es', label: 'Español' },
  { value: 'nb', label: 'Norsk Bokmål' },
  { value: 'nl', label: 'Nederlands' },
  { value: 'de', label: 'Deutsch' },
  { value: 'fi', label: 'Suomi' },
  { value: 'da', label: 'Dansk' },
  { value: 'he', label: 'עברית' },
  { value: 'bg', label: 'Български' },
  { value: 'pt', label: 'Português' },
];

const PreferencesSetStep: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const onboarding = useSelector((state: RootState) => state.onboarding);
  const [skipPreferences, setSkipPreferences] = useState(false);
  const [preferencesData, setPreferencesData] = useState({
    general_settings: {
      language: 'en',
      theme: 'system',
    },
    ai_settings: {
      // Unset by design - onboarding is where the user actively picks their
      // jurisdiction and AI output language, so preselecting either would
      // answer for them. Both are optional; skipping leaves them unset.
      jurisdiction: '',
      language: '',
    },
  });
  const [aiLanguages, setAiLanguages] = useState<DropdownOption[]>([]);
  const [jurisdictionOptions, setJurisdictionOptions] = useState<DropdownOption[]>([]);

  // Fetch AI languages and jurisdictions from backend
  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const dropdownsData = await dispatch(fetchMatterDropdowns());
        if (dropdownsData) {
          setAiLanguages(dropdownsData.languages || []);
          // Jurisdictions come from storage-account discovery (backend); fall
          // back to the static list only when the backend list is empty.
          setJurisdictionOptions(
            dropdownsData.jurisdictions && dropdownsData.jurisdictions.length > 0
              ? dropdownsData.jurisdictions
              : AVAILABLE_JURISDICTIONS,
          );
        }
      } catch (error) {
        console.error('Failed to fetch dropdowns:', error);
      }
    };

    fetchDropdowns();
  }, [dispatch]);

  // Guard: This step should only be accessible if organization is set
  // Other fields (first_name, last_name, role) are validated at the page level
  if (!onboarding.user?.organization_id) {
    return (
      <OnboardingStepCard
        title={t('Set your preferences')}
        description={t('Please complete the organization and profile steps first.')}
      />
    );
  }

  const applyThemeToDOM = (theme: string) => {
    const root = window.document.documentElement;
    let themeToApply = theme;

    // If system theme is selected, detect the user's preference
    if (theme === 'system') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      themeToApply = prefersDark ? 'dark' : 'light';
    }

    root.classList.remove('light', 'dark');
    root.classList.add(themeToApply);
    localStorage.setItem('theme', theme);
  };

  const handleGeneralSettingChange = (field: string, value: any) => {
    setPreferencesData((prev) => ({
      ...prev,
      general_settings: {
        ...prev.general_settings,
        [field]: value,
      },
    }));

    // Apply theme immediately to the DOM
    if (field === 'theme') {
      applyThemeToDOM(value);
    }

    // Apply language change immediately
    if (field === 'language') {
      changeLanguage(value);
    }
  };

  const handleAISettingChange = (field: string, value: any) => {
    setPreferencesData((prev) => ({
      ...prev,
      ai_settings: {
        ...prev.ai_settings,
        [field]: value,
      },
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (!skipPreferences) {
        await dispatch(setPreferences(preferencesData) as any);
      } else {
        // If skipping preferences, still dispatch success to mark onboarding as complete
        dispatch({
          type: 'ONBOARDING_SET_PREFERENCES_SUCCESS',
          payload: {
            preferences: preferencesData,
          },
        });
      }

      // Mark onboarding as completed in the backend
      await dispatch(completeOnboarding() as any);

      setTimeout(() => {
        window.location.href = '/';
      }, 1000);
    } catch (error) {
      console.error('Error completing onboarding:', error);
    }
  };

  return (
    <OnboardingStepCard
      as="form"
      onSubmit={handleSubmit}
      title={t('Set your preferences')}
      description={t('Choose how PONS looks and which jurisdiction and language it works in. You can change all of this later in Settings.')}
      footer={
        <FormActions>
          <Button
            type="submit"
            variant="primary"
            size="small"
            disabled={onboarding.isLoading}
            isLoading={onboarding.isLoading}
          >
            {t('Complete Setup')}
          </Button>
        </FormActions>
      }
    >
      {/* Two labelled groups rather than two `FormSection` cards: this body is
          already inside the step's glass card, and a card inside a card is the
          one container shape FORM_SPEC §8 rules out. A group caption takes the
          field label style. */}
      <div className="space-y-4">
        <p className={FIELD_LABEL_CLASS}>{t('Platform Settings')}</p>

        <Dropdown
          label={t('Language')}
          placeholder={t('Select')}
          options={SUPPORTED_LANGUAGES}
          value={preferencesData.general_settings.language}
          fullWidth={true}
          onChange={(value) => handleGeneralSettingChange('language', value)}
        />

        <FieldShell label={t('Theme')} translate={false}>
          <div className="flex gap-2">
            {[
              { value: 'light', icon: PiSunLight, label: t('Light') },
              { value: 'dark', icon: PiMoonLight, label: t('Dark') },
              { value: 'system', icon: PiDesktopLight, label: t('System') },
            ].map(({ value, icon: Icon, label }) => (
              <button
                key={value}
                type="button"
                onClick={() => handleGeneralSettingChange('theme', value)}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md border transition-colors duration-200 text-sm font-medium ${FOCUS_RING} ${
                  preferencesData.general_settings.theme === value
                    ? `${SELECTED_CARD} text-fg-strong`
                    : 'border-hairline bg-surface-sunken/70 text-fg-muted hover:bg-surface-sunken'
                }`}
                aria-pressed={preferencesData.general_settings.theme === value}
                title={label}
              >
                <Icon size={16} />
                <span className="hidden sm:inline">{label}</span>
              </button>
            ))}
          </div>
        </FieldShell>
      </div>

      <div className="space-y-4 border-t border-hairline-soft pt-4">
        <p className={FIELD_LABEL_CLASS}>{t('AI Settings')}</p>

        <Dropdown
          label={t('Jurisdiction')}
          placeholder={t('Select Jurisdiction')}
          options={jurisdictionOptions}
          value={preferencesData.ai_settings.jurisdiction}
          onChange={(value) => handleAISettingChange('jurisdiction', value)}
          fullWidth={true}
        />

        <Dropdown
          label={t('AI Language')}
          placeholder={t('Select')}
          options={aiLanguages}
          value={preferencesData.ai_settings.language}
          onChange={(value) => handleAISettingChange('language', value)}
          fullWidth={true}
        />
      </div>

      <Well>
        <CheckBox
          id="skip-prefs"
          label={t('Skip preferences for now')}
          translate={false}
          checked={skipPreferences}
          onChange={(e) => setSkipPreferences(e.target.checked)}
        />
      </Well>
    </OnboardingStepCard>
  );
};

export default PreferencesSetStep;
